import React, {useState, useEffect, useMemo} from 'react';
import moment from 'moment';
import {totalizar} from "../../../lib/fazendaRentavel";
import {FazendaObject, getFazendaNomeCompleto} from "../../../models/Fazenda";
import {IonButton, IonIcon, IonAlert, IonLoading} from "@ionic/react";
import {mailOutline, createOutline} from "ionicons/icons";
import FieldView from "../../../components/FieldView";
import {formatNumber, validateEmail} from "../../../lib/utils";
import {post} from "../../../utils/frRequest";
import {useAppDispatch} from "../../../lib/hooks";
import {actions} from "../../../lib/app";
import trim from 'lodash/trim';


interface CalendarioInstanceProps {
  fazenda: FazendaObject;
  produtos: any[];
  rebanho: any[];
  calendario: any[];
  created_at: moment.Moment;
  updated_at: moment.Moment;
  setEdit: () => void;
}

const CalendarioInstance: React.FC<CalendarioInstanceProps> = (props) => {
  const [enviarIsOpen, setEnviarIsOpen] = useState(false);
  const [enviarEmail, setEnviarEmail] = useState(false);
  const [enviandoEmail, setEnviandoEmail] = useState(false);
  const dispatch = useAppDispatch();

  const [email, setEmail] = useState<string>();
  const fazendaId = props.fazenda.id;
  const calendario = props.calendario;

  const totalizado: any = useMemo(() => {
    return totalizar(
      getFazendaNomeCompleto(props.fazenda),
      props.calendario,
      props.rebanho,
      props.produtos,
    )
  }, [props]);

  // enviar email
  useEffect(() => {
    if (enviarEmail && !enviandoEmail) {
      setEnviandoEmail(true);

      post('/veApi/vet_experts/enviar_email', {
          fazendaId,
          email,
          simulacao: { ...totalizado, data: totalizado.data.format('YYYY-MM-DD') },
          calendario,
      })
        .then(({success, error}) => {
          if (!success) {
            console.error(`Erro inesperado em enviar_email: ${error}`);
            dispatch(actions.notifyError(`Ocorreu um erro inesperado ao enviar o email: ${error}`));
          } else {
            dispatch(actions.notifySuccess('Email enviado com sucesso.'));
          }
        })
        .finally(() => {
          setEnviarEmail(false);
          setEnviandoEmail(false);
        });
    }
  }, [fazendaId, calendario, enviarEmail, enviandoEmail, email, totalizado, dispatch]);

  const onClickEnviar = (values: any) => {
    // validar email
    const _email = trim(values.email).toLowerCase();
    if (_email) {
      if (!validateEmail(_email)) {
        dispatch(actions.notifyError('Email inválido'));
        return false;
      }
    }
    
    setEmail(_email);
    setEnviarEmail(true);
    setEnviarIsOpen(false);
  }

  return (
    <div>
      <IonLoading
        isOpen={enviandoEmail}
        message={'Enviando email...'}
      />
      <div className="ion-padding">
        <div className="font-bold mb-4 text-xl">
          Calendário sanitário
        </div>
        <FieldView
          label="Fazenda"
          value={getFazendaNomeCompleto(props.fazenda)}
        />

        <div className="flex justify-between align-top">
          <FieldView
            label="Criado em"
            type="date"
            value={props.created_at}
          />
          <FieldView
            label="Última atualização"
            type="date"
            value={props.updated_at}
          />
        </div>

        <div className="mt-4 flex w-full justify-end">
          <IonButton
            fill="clear"
            onClick={() => setEnviarIsOpen(true)}
          >
            <IonIcon icon={mailOutline} slot="start"/>
            Enviar
          </IonButton>
          <IonButton
            fill="clear"
            onClick={props.setEdit}
          >
            <IonIcon icon={createOutline} slot="start"/>
            Alterar
          </IonButton>
        </div>

        <div className="mt-8">
          <div className="font-bold mb-4 text-xl">
            Resumo do investimento
          </div>
          <FieldView
            label="Investimento total"
            type="currency"
            value={totalizado.total_geral}
          />
          <FieldView
            label="Total de animais"
            type="number"
            value={totalizado.total_animais}
          />
          <FieldView
            label="Investimento médio por animal"
            type="currency"
            value={totalizado.total_geral / totalizado.total_animais}
          />
        </div>

        <div className="mt-12">
          <div className="font-bold mb-4 text-xl">
            Produtos utilizados
          </div>

          {
            Object.entries(totalizado.produtos).map(i => {
              const k = i[0];
              const p: any = i[1];
              return (
                <Card key={`utilizados-${k}`} title={p.nome}>
                  <CardValue
                    label="mls necessários"
                    value={formatNumber(p.total_ml, 0)}
                  />

                  <CardValue
                    label="frasco(s)"
                    value={p.total_frascos}
                  />

                  <CardValue
                    label="Valor total"
                    value={`R$${formatNumber(p.total)}`}
                  />
                </Card>
              )
            })
          }
        </div>

        <div className="mt-12">
          <div className="font-bold mb-4 text-xl">
            Visão por categoria
          </div>

          {
            Object.entries(totalizado.categorias).map(i => {
              const k = i[0];
              const c: any = i[1];
              return (
                <Card key={`categorias-${k}`} title={c.nome}>
                  <CardValue
                    label="cabeças"
                    value={formatNumber(c.num_animais, 0)}
                  />

                  <CardValue
                    label="por cabeça"
                    value={`R$${formatNumber(c.total / c.num_animais)}`}
                  />

                  <CardValue
                    label="Valor total"
                    value={`R$${formatNumber(c.total)}`}
                  />
                </Card>
              )
            })
          }
        </div>

        <div className="mt-12">
          <div className="font-bold mb-4 text-xl">
            Observações
          </div>

          {
            totalizado.observacoes.map((o: any) => (
              <div key={o.nome} className="mb-3">
                <div>
                  {o.nome}
                </div>
                <div className="text-gray-600">
                  {o.texto}
                </div>
              </div>
            ))
          }
        </div>
      </div>

      <IonAlert
          isOpen={enviarIsOpen}
          message="Digite o email do seu cliente abaixo, você receberá uma cópia também. Deixe em branco para enviar apenas para você."
          onDidDismiss={() => {
            setEnviarIsOpen(false);
          }}
          header={'Enviar PDF por email'}
          inputs={[
            {
              name: 'email',
              label: 'Email',
              type: 'email',
              placeholder: 'Email',
              value: email,
            },
          ]}
          buttons={[
            {
              text: 'Cancelar',
              role: 'cancel',
              cssClass: 'secondary',
            },
            {
              text: 'Enviar',
              handler: onClickEnviar
            }
          ]}
        />
    </div>
  );
}

export default CalendarioInstance;


interface CardProps {
  title: string;
}

const Card: React.FC<CardProps> = ({children, title}) => {
  return (
    <div className="p-4 shadow-md rounded-md mb-8">
      <div className="font-bold mb-2">
        {title}
      </div>
      {children}
    </div>
  )
}

interface CardValueProps {
  label: string;
  value: string;
}

const CardValue: React.FC<CardValueProps> = ({label, value}) => {
  return (
    <div className="flex mb-1">
      <div className="w-4/12 mr-4 text-right">
        {value}
      </div>
      <div className="text-gray-600">
        {label}
      </div>
    </div>
  )
}